/**
 * File: PeerReviewFeedbackControl.tsx
 * Description: Compact thumbs-up / thumbs-down + optional comment control used
 *   to capture reviewer feedback on an individual debate message. Feedback is
 *   persisted through the shared feedback pipeline (section: peer_review).
 * Author: GitHub Copilot
 */
import {
  HandThumbDownIcon as HandThumbDownOutline,
  HandThumbUpIcon as HandThumbUpOutline,
} from '@heroicons/react/24/outline';
import {
  HandThumbDownIcon as HandThumbDownSolid,
  HandThumbUpIcon as HandThumbUpSolid,
} from '@heroicons/react/24/solid';
import React, { useState } from 'react';

import {
  FeedbackData,
  FeedbackService,
} from '../../services/feedbackService';

const PEER_REVIEW_SECTION = 'peer_review';
const FEEDBACK_THUMBS_UP = 'thumbs_up';
const FEEDBACK_THUMBS_DOWN = 'thumbs_down';
const FEEDBACK_SAVED_MESSAGE = 'Thanks for the feedback!';
const FEEDBACK_FAILED_MESSAGE = 'Could not save feedback.';
const COMMENT_PLACEHOLDER = 'Add an optional comment…';

type FeedbackType = typeof FEEDBACK_THUMBS_UP | typeof FEEDBACK_THUMBS_DOWN;

interface PeerReviewFeedbackControlProps {
  projectId: number;
  agent: string;
  context: Record<string, unknown>;
}

/**
 * Inline feedback control for a single agent debate message.
 */
export const PeerReviewFeedbackControl: React.FC<
  PeerReviewFeedbackControlProps
> = ({ projectId, agent, context }) => {
  const [selected, setSelected] = useState<FeedbackType | null>(null);
  const [comment, setComment] = useState('');
  const [status, setStatus] = useState<'idle' | 'saved' | 'error'>('idle');
  const [saving, setSaving] = useState(false);

  const submit = async (feedbackType: FeedbackType, note: string) => {
    if (!projectId) {
      return;
    }
    setSaving(true);
    try {
      const feedbackData: FeedbackData = {
        feedback_type: feedbackType,
        comment: note,
        ...context,
      };
      await FeedbackService.submitFeedback({
        project_id: projectId,
        section: PEER_REVIEW_SECTION,
        agent,
        feedback_data: feedbackData,
      });
      setStatus('saved');
    } catch {
      setStatus('error');
    } finally {
      setSaving(false);
    }
  };

  const handleVote = (feedbackType: FeedbackType) => {
    setSelected(feedbackType);
    setStatus('idle');
    void submit(feedbackType, comment.trim());
  };

  const handleCommentBlur = () => {
    const trimmed = comment.trim();
    if (selected && trimmed) {
      void submit(selected, trimmed);
    }
  };

  const buttonClass = (active: boolean) =>
    `flex h-6 w-6 items-center justify-center rounded-md border transition-colors ${
      active
        ? 'border-gray-400 bg-gray-100'
        : 'border-transparent hover:border-gray-300 hover:bg-gray-50'
    } ${saving ? 'cursor-wait opacity-70' : 'cursor-pointer'}`;

  const ThumbUp =
    selected === FEEDBACK_THUMBS_UP ? HandThumbUpSolid : HandThumbUpOutline;
  const ThumbDown =
    selected === FEEDBACK_THUMBS_DOWN
      ? HandThumbDownSolid
      : HandThumbDownOutline;

  return (
    <div className="mt-1.5 flex flex-col gap-1">
      <div className="flex items-center gap-1">
        <button
          type="button"
          disabled={saving}
          onClick={() => handleVote(FEEDBACK_THUMBS_UP)}
          aria-label="Helpful"
          aria-pressed={selected === FEEDBACK_THUMBS_UP}
          className={buttonClass(selected === FEEDBACK_THUMBS_UP)}
        >
          <ThumbUp className="h-3.5 w-3.5 text-green-700" />
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={() => handleVote(FEEDBACK_THUMBS_DOWN)}
          aria-label="Not helpful"
          aria-pressed={selected === FEEDBACK_THUMBS_DOWN}
          className={buttonClass(selected === FEEDBACK_THUMBS_DOWN)}
        >
          <ThumbDown className="h-3.5 w-3.5 text-red-600" />
        </button>
        {status === 'saved' && (
          <span className="text-[11px] text-green-600">
            {FEEDBACK_SAVED_MESSAGE}
          </span>
        )}
        {status === 'error' && (
          <span className="text-[11px] text-red-600">
            {FEEDBACK_FAILED_MESSAGE}
          </span>
        )}
      </div>
      {selected && (
        <input
          type="text"
          value={comment}
          onChange={(event) => setComment(event.target.value)}
          onBlur={handleCommentBlur}
          placeholder={COMMENT_PLACEHOLDER}
          aria-label="Feedback comment"
          className="w-full rounded-md border border-gray-200 bg-white px-2 py-1 text-[11px] text-gray-700 shadow-sm focus:border-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-300"
        />
      )}
    </div>
  );
};
